import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiBookmark, FiEye, FiCheckCircle, FiUserX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function StaffNotice() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReadersModal, setShowReadersModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [readers, setReaders] = useState({ readers: [], unreadUsers: [], readCount: 0, totalStaff: 0, readRate: 0 });
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isPinned: false,
    targetRoles: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/api/staff-notice');
      setNotices(res.data.notices || []);
    } catch (error) {
      console.error('Failed to load notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingNotice(null);
    setFormData({ title: '', content: '', isPinned: false, targetRoles: [] });
    setShowModal(true);
  };

  const openEditModal = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      isPinned: notice.isPinned || false,
      targetRoles: notice.targetRoles || []
    });
    setShowModal(true);
  };

  const openDetail = async (notice) => {
    setSelectedNotice(notice);
    setShowDetailModal(true);

    // Mark as read
    if (!notice.isRead) {
      try {
        await api.post(`/api/staff-notice/${notice._id}/read`);
        loadData();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const openReaders = async (notice) => {
    try {
      const res = await api.get(`/api/staff-notice/${notice._id}/readers`);
      setReaders(res.data);
      setSelectedNotice(notice);
      setShowReadersModal(true);
    } catch (error) {
      console.error('Failed to load readers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingNotice) {
        await api.put(`/api/staff-notice/${editingNotice._id}`, formData);
        alert('수정되었습니다');
      } else {
        await api.post('/api/staff-notice', formData);
        alert('공지가 등록되었습니다');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const handleDelete = async (notice) => {
    if (!window.confirm('이 공지를 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/staff-notice/${notice._id}`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  // Stats
  const stats = {
    total: notices.length,
    unread: notices.filter(n => !n.isRead).length,
    pinned: notices.filter(n => n.isPinned).length
  };

  return (
    <div className="staff-notice-page">
      <div className="page-header">
        <h2>내부 공지</h2>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FiPlus /> 공지 작성
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 공지</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.unread}</div>
          <div className="stat-label">안 읽음</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pinned}</div>
          <div className="stat-label">고정 공지</div>
        </div>
      </div>

      {/* Notices List */}
      <div className="notice-list">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : notices.length === 0 ? (
          <div className="empty">등록된 공지가 없습니다</div>
        ) : (
          notices.map((notice) => (
            <div
              key={notice._id}
              className={`notice-item ${notice.isPinned ? 'pinned' : ''} ${!notice.isRead ? 'unread' : ''}`}
              onClick={() => openDetail(notice)}
            >
              <div className="notice-main">
                <div className="notice-header">
                  {notice.isPinned && <FiBookmark className="pin-icon" />}
                  <h4 className="notice-title">{notice.title}</h4>
                  {!notice.isRead && <span className="badge badge-primary">NEW</span>}
                </div>
                <p className="notice-preview">{notice.content.substring(0, 100)}...</p>
                <div className="notice-meta">
                  <span>{notice.author?.name || '관리자'}</span>
                  <span>{format(new Date(notice.createdAt), 'yyyy-MM-dd HH:mm')}</span>
                  {isAdmin && (
                    <span className="read-count" onClick={(e) => { e.stopPropagation(); openReaders(notice); }}>
                      <FiEye /> {notice.readCount || 0}명 읽음
                      {notice.totalTarget > 0 && notice.totalTarget > (notice.readCount || 0) && (
                        <span className="unread-indicator">
                          <FiUserX /> {notice.totalTarget - (notice.readCount || 0)}명 미확인
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="notice-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn btn-icon" onClick={() => openEditModal(notice)}>
                    <FiEdit2 />
                  </button>
                  <button className="btn btn-icon btn-danger" onClick={() => handleDelete(notice)}>
                    <FiTrash2 />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingNotice ? '공지 수정' : '공지 작성'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>제목 *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="공지 제목"
                  required
                />
              </div>
              <div className="form-group">
                <label>내용 *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="공지 내용을 입력하세요"
                  rows={10}
                  required
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPinned}
                    onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                  />
                  <FiBookmark /> 상단 고정
                </label>
              </div>
              <div className="form-group">
                <label>대상 선택 (선택하지 않으면 전체)</label>
                <div className="target-roles-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.targetRoles.includes('admin')}
                      onChange={(e) => {
                        const roles = e.target.checked
                          ? [...formData.targetRoles, 'admin']
                          : formData.targetRoles.filter(r => r !== 'admin');
                        setFormData({ ...formData, targetRoles: roles });
                      }}
                    />
                    관리자
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.targetRoles.includes('teacher')}
                      onChange={(e) => {
                        const roles = e.target.checked
                          ? [...formData.targetRoles, 'teacher']
                          : formData.targetRoles.filter(r => r !== 'teacher');
                        setFormData({ ...formData, targetRoles: roles });
                      }}
                    />
                    강사
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.targetRoles.includes('assistant')}
                      onChange={(e) => {
                        const roles = e.target.checked
                          ? [...formData.targetRoles, 'assistant']
                          : formData.targetRoles.filter(r => r !== 'assistant');
                        setFormData({ ...formData, targetRoles: roles });
                      }}
                    />
                    조교
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingNotice ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedNotice && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedNotice.isPinned && <FiBookmark className="pin-icon" />}
                {selectedNotice.title}
              </h3>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="notice-detail">
              <div className="notice-detail-meta">
                <span>작성자: {selectedNotice.author?.name || '관리자'}</span>
                <span>작성일: {format(new Date(selectedNotice.createdAt), 'yyyy-MM-dd HH:mm')}</span>
                {selectedNotice.targetRoles?.length > 0 && (
                  <span className="target-info">
                    대상: {selectedNotice.targetRoles.map(r =>
                      r === 'admin' ? '관리자' : r === 'teacher' ? '강사' : '조교'
                    ).join(', ')}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="notice-read-summary">
                  <FiEye />
                  <span>{selectedNotice.readCount || 0}명 읽음</span>
                  {selectedNotice.totalTarget > 0 && (
                    <span className="unread-count">
                      / {selectedNotice.totalTarget - (selectedNotice.readCount || 0)}명 안 읽음
                    </span>
                  )}
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={(e) => { e.stopPropagation(); setShowDetailModal(false); openReaders(selectedNotice); }}
                  >
                    상세보기
                  </button>
                </div>
              )}
              <div className="notice-detail-content">
                {selectedNotice.content.split('\n').map((line, i) => (
                  <p key={i}>{line || <br />}</p>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Readers Modal */}
      {showReadersModal && selectedNotice && (
        <div className="modal-overlay" onClick={() => setShowReadersModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>읽음 현황</h3>
              <button className="close-btn" onClick={() => setShowReadersModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="readers-summary">
              <div className="read-rate">
                <FiCheckCircle /> {readers.readCount}/{readers.totalStaff}명 ({readers.readRate}%)
              </div>
            </div>
            <div className="readers-columns">
              {/* 읽은 사람 */}
              <div className="readers-column">
                <h4><FiCheckCircle className="icon-success" /> 읽음 ({readers.readers?.length || 0})</h4>
                <div className="readers-list">
                  {readers.readers?.length === 0 ? (
                    <div className="empty">아직 읽은 사람이 없습니다</div>
                  ) : (
                    readers.readers.map((r, i) => (
                      <div key={i} className="reader-item read">
                        <span className="reader-name">{r.user?.name || '-'}</span>
                        <span className="reader-role">
                          {r.user?.role === 'admin' ? '관리자' : r.user?.role === 'teacher' ? '강사' : '조교'}
                        </span>
                        <span className="read-time">{format(new Date(r.readAt), 'MM/dd HH:mm')}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {/* 안 읽은 사람 */}
              <div className="readers-column">
                <h4><FiUserX className="icon-danger" /> 안 읽음 ({readers.unreadUsers?.length || 0})</h4>
                <div className="readers-list">
                  {readers.unreadUsers?.length === 0 ? (
                    <div className="empty">모두 읽었습니다</div>
                  ) : (
                    readers.unreadUsers.map((u, i) => (
                      <div key={i} className="reader-item unread">
                        <span className="reader-name">{u.name || '-'}</span>
                        <span className="reader-role">
                          {u.role === 'admin' ? '관리자' : u.role === 'teacher' ? '강사' : '조교'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReadersModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
